import { useState } from 'react';
import { useIntl } from 'react-intl';

import Select from '../Select';
import {
    AegApprovalTypeEnum,
    AegServiceTypeEnum,
    AegVehicleTypeEnum,
    formatAegAssignTo,
    formatAegEnum,
    formatAegStage,
    getAegAssignToOptions,
    type StageKey,
} from '../../types';
import './styles.css';

export type Filters = {
    query: string;
    dealer: string;
    regional: string;
    // cell: string;
    approval: string;
    vehicleType: string;
    serviceType: string;
    owner: string;
    assignedTo: string;
};

export const emptyFilters: Filters = {
    query: '',
    dealer: '',
    regional: '',
    // cell: '',
    approval: '',
    vehicleType: '',
    serviceType: '',
    owner: '',
    assignedTo: '',
};

type FilterProps = {
    filters: Filters;
    stage: StageKey | null;
    onFiltersChange: (filters: Filters) => void;
    onStageChange: (stage: StageKey | null) => void;
};

export default function Filter({ filters, stage, onFiltersChange, onStageChange }: FilterProps) {
    const intl = useIntl();
    const [showMore, setShowMore] = useState(false);
    const update = (key: keyof Filters, value: string) => onFiltersChange({ ...filters, [key]: value });
    const activeChips = Object.entries(filters).filter(([, value]) => value);
    const chipLabel = (key: string, value: string) => {
        switch (key) {
            case 'dealer':
                return intl.formatMessage({ id: 'intl-msg:support.aeg.filter.chip.dn' }, { value });
            case 'regional':
                return intl.formatMessage({ id: 'intl-msg:support.aeg.filter.chip.regional' }, { value });
            case 'owner':
                return intl.formatMessage({ id: 'intl-msg:support.aeg.filter.chip.owner' }, { value });
            case 'approval':
                return formatAegEnum(intl, 'approvalType', value);
            case 'vehicleType':
                return formatAegEnum(intl, 'vehicleType', value);
            case 'serviceType':
                return formatAegEnum(intl, 'serviceType', value);
            case 'assignedTo':
                return formatAegAssignTo(intl, value);
            default:
                return value;
        }
    };

    return (
        <>
            <section className='aeg-filter-root panel panel-default margin-bottom-0 rounded text-color-darkest bg-white'>
                <div className='aeg-filter-toolbar display-flex align-items-center flex-wrap gap-10 padding-15'>
                    <label className='aeg-filter-search bg-white margin-0'>
                        <input
                            className='bg-transparent text-color-darkest'
                            id='aeg-search'
                            placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.search.placeholder' })}
                            type='search'
                            value={filters.query}
                            onChange={event => update('query', event.target.value)}
                        />
                        <kbd className='text-color-dark'>/</kbd>
                    </label>
                    <input
                        aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.dn' })}
                        className='aeg-filter-text form-control'
                        onChange={event => update('dealer', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.dn' })}
                        type='search'
                        value={filters.dealer}
                    />
                    <input
                        aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.regional' })}
                        className='aeg-filter-text form-control'
                        onChange={event => update('regional', event.target.value)}
                        placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.regional' })}
                        type='search'
                        value={filters.regional}
                    />
                    {/* <Select
                        ariaLabel='Célula'
                        emptyLabel='Todas'
                        onChange={value => update('cell', value)}
                        options={toOptions(unique(rows, 'cell'))}
                        prefix='Célula'
                        value={filters.cell}
                    /> */}
                    <Select
                        ariaLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.approval' })}
                        emptyLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.allFeminine' })}
                        onChange={value => update('approval', value)}
                        options={Object.values(AegApprovalTypeEnum).map(value => ({
                            value,
                            label: formatAegEnum(intl, 'approvalType', value),
                        }))}
                        prefix={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.approval' })}
                        value={filters.approval}
                    />
                    <Select
                        ariaLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.vehicle' })}
                        emptyLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.allMasculine' })}
                        onChange={value => update('vehicleType', value)}
                        options={Object.values(AegVehicleTypeEnum).map(value => ({
                            value,
                            label: formatAegEnum(intl, 'vehicleType', value),
                        }))}
                        prefix={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.vehicle' })}
                        value={filters.vehicleType}
                    />
                    <button className='btn btn-default' onClick={() => setShowMore(true)} type='button'>
                        {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.moreFilters' })}
                    </button>
                </div>
                {activeChips.length > 0 || stage ? (
                    <div className='display-flex align-items-center flex-wrap gap-10 padding-x-15 padding-bottom-10'>
                        {activeChips.map(([key, value]) => (
                            <span className='aeg-filter-chip bg-highlight-lighter text-color-highlight-dark' key={key}>
                                {chipLabel(key, value)}
                                <button
                                    aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.removeFilter' })}
                                    className='bg-transparent text-color-highlight-dark'
                                    onClick={() => update(key as keyof Filters, '')}
                                    type='button'
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                        {stage ? (
                            <span className='aeg-filter-chip bg-highlight-lighter text-color-highlight-dark'>
                                {formatAegStage(intl, stage)}
                                <button
                                    aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.removeStage' })}
                                    className='bg-transparent text-color-highlight-dark'
                                    onClick={() => onStageChange(null)}
                                    type='button'
                                >
                                    ×
                                </button>
                            </span>
                        ) : null}
                        <button
                            className='btn btn-default'
                            onClick={() => {
                                onFiltersChange(emptyFilters);
                                onStageChange(null);
                            }}
                            type='button'
                        >
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.clearAll' })}
                        </button>
                    </div>
                ) : null}
            </section>

            {showMore ? (
                <>
                    <div className='aeg-filter-scrim bg-black opacity-50' onClick={() => setShowMore(false)} />
                    <aside className='aeg-filter-drawer bg-white'>
                        <header className='display-flex align-items-center gap-10 padding-20 border border-bottom-only border-left-none border-right-none border-top-none'>
                            <div>
                                <h2 className='aeg-filter-drawer-title margin-0 text-bold text-color-darkest'>
                                    {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.drawer.title' })}
                                </h2>
                                <span className='display-block margin-top-5 text-size-12 text-color-dark'>
                                    {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.drawer.subtitle' })}
                                </span>
                            </div>
                            <button
                                aria-label={intl.formatMessage({ id: 'intl-msg:support.aeg.common.close' })}
                                className='btn btn-link margin-left-auto padding-0'
                                onClick={() => setShowMore(false)}
                                type='button'
                            >
                                <span aria-hidden='true' className='aeg-filter-close-icon rioglyph rioglyph-remove' />
                            </button>
                        </header>
                        <div className='display-flex flex-column gap-20 flex-1-1 padding-20 overflow-auto'>
                            <label className='form-group margin-bottom-0 display-flex flex-column gap-5'>
                                <span className='text-size-12 text-medium text-color-darker'>{intl.formatMessage({ id: 'intl-msg:support.aeg.filter.serviceType' })}</span>
                                <Select
                                    ariaLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.serviceType' })}
                                    emptyLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.allTypes' })}
                                    fullWidth
                                    onChange={value => update('serviceType', value)}
                                    options={Object.values(AegServiceTypeEnum).map(value => ({
                                        value,
                                        label: formatAegEnum(intl, 'serviceType', value),
                                    }))}
                                    value={filters.serviceType}
                                />
                            </label>
                            <label className='form-group margin-bottom-0 display-flex flex-column gap-5'>
                                <span className='text-size-12 text-medium text-color-darker'>{intl.formatMessage({ id: 'intl-msg:support.aeg.filter.owner' })}</span>
                                <input
                                    className='form-control height-40'
                                    placeholder={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.ownerPlaceholder' })}
                                    value={filters.owner}
                                    onChange={event => update('owner', event.target.value)}
                                />
                            </label>
                            <label className='form-group margin-bottom-0 display-flex flex-column gap-5'>
                                <span className='text-size-12 text-medium text-color-darker'>{intl.formatMessage({ id: 'intl-msg:support.aeg.filter.assignments' })}</span>
                                <Select
                                    ariaLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.assignments' })}
                                    emptyLabel={intl.formatMessage({ id: 'intl-msg:support.aeg.filter.allFeminine' })}
                                    fullWidth
                                    onChange={value => update('assignedTo', value)}
                                    options={getAegAssignToOptions(intl)}
                                    value={filters.assignedTo}
                                />
                            </label>
                        </div>
                        <footer className='display-grid grid-cols-2 gap-10 padding-20 border border-top-only border-left-none border-right-none border-bottom-none'>
                            <button
                                className='btn btn-default width-100pct height-40 justify-content-center'
                                onClick={() =>
                                    onFiltersChange({
                                        ...filters,
                                        serviceType: '',
                                        owner: '',
                                        assignedTo: '',
                                    })
                                }
                                type='button'
                            >
                                {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.clear' })}
                            </button>
                            <button
                                className='btn btn-primary width-100pct height-40 justify-content-center'
                                onClick={() => setShowMore(false)}
                                type='button'
                            >
                                {intl.formatMessage({ id: 'intl-msg:support.aeg.filter.apply' })}
                            </button>
                        </footer>
                    </aside>
                </>
            ) : null}
        </>
    );
}
